import { useTranslation } from 'react-i18next'
import { useConsent } from '../../consent/useConsent'
import { APP_NAME } from '../../constants/brand'
import { META_PRIVACY_URL, META_TERMS_URL } from '../../constants/legal'
import { createNadDebug } from '../../utils/debugLog'

// NAD feature tag for the persistent landing legal links.
const log = createNadDebug('dw:landing:footer-legal')

const legalLinkClass =
  'font-sans text-xs font-medium text-[#a1a1aa] underline underline-offset-2 hover:text-white md:text-sm'

/**
 * The "|" between legal links. Purely decorative, so it is hidden from
 * assistive technology — a screen reader should read "Meta Terms", not
 * "Meta Terms vertical line Privacy Policy".
 */
function Separator() {
  return (
    <span aria-hidden="true" className="font-sans text-xs font-medium text-[#52525b] md:text-sm">
      |
    </span>
  )
}

export default function FooterV2() {
  const { t } = useTranslation('landing')
  const { openSettings } = useConsent()
  return (
    <footer className="border-t border-white/[0.08] bg-[#0d0d0d] px-4 pb-10 pt-16 sm:px-6 md:px-20 md:pt-20">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-10">
        {/* Top: brand + description */}
        <div className="flex flex-col gap-8 md:flex-row md:gap-20">
          <div className="flex w-full max-w-[360px] flex-col gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/landing-v2/logo-icon.png"
                alt={APP_NAME}
                className="w-[22px] h-[22px] object-contain"
              />
              <span className="font-[Fustat] font-semibold text-xl text-white opacity-[0.92]">
                {APP_NAME}
              </span>
            </div>
            <p className="font-sans text-sm text-[#a1a1aa] leading-[1.6]">
              {t('footerV2.brandDescription')}
            </p>
          </div>
        </div>

        {/* Bottom bar. The copyright keeps its original left-hand slot; the
            legal links sit centred in the bar as a "Link | Link | Link" row,
            matching the reference footer in Workplace post 1620362009721096.
            A three-column grid — [1fr, auto, 1fr] with the third column empty —
            is what centres the links against the BAR rather than against the
            space left over by the copyright; `minmax(0,1fr)` keeps the outer
            columns equal so a long translated copyright can't shift the centre.
            Below `sm` the two stack. The legal links are here so Terms and
            Privacy Policy are reachable without starting sign-up; Cookie
            settings is the persistent entry point to revisit/withdraw cookie
            consent (GDPR Art. 7: withdraw as easily as given). */}
        <div className="grid grid-cols-1 gap-3 border-t border-white/[0.08] pt-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center sm:gap-4">
          <span
            data-testid="footer-copyright"
            className="font-sans text-xs font-medium text-[#a1a1aa] md:text-sm"
          >
            {t('footerV2.copyright', { appName: APP_NAME })}
          </span>
          <div
            data-testid="footer-legal-links"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2"
          >
            <a
              href={META_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-meta-terms"
              onClick={() => log({ event: 'footer_meta_terms_click', url: META_TERMS_URL })}
              className={legalLinkClass}
            >
              {t('footerV2.metaTerms')}
            </a>
            <Separator />
            <a
              href={META_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-privacy-policy"
              onClick={() => log({ event: 'footer_privacy_policy_click', url: META_PRIVACY_URL })}
              className={legalLinkClass}
            >
              {t('footerV2.privacyPolicy')}
            </a>
            <Separator />
            <button
              type="button"
              onClick={openSettings}
              data-testid="footer-cookie-settings"
              className={`${legalLinkClass} cursor-pointer`}
            >
              {t('footerV2.cookieSettings')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
